/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { diffDays, isLessOrEqual } from '../day-date';
import Stringify from '../stringify';

/**
 * @private
 * @typedef {import('../day-date').DayDate} DayDate
 */

// TODO: Vyřešit otypování konstruktorů.
// - https://www.typescriptlang.org/docs/handbook/advanced-types.html#polymorphic-this-types
// - https://github.com/Microsoft/TypeScript/issues/3841
// - https://stackoverflow.com/questions/55866473/infer-constructor-type-in-typescript#comment98399515_55867277
/**
 * @template {DayInterval} T
 * @typedef {{ new (o: T): T }} DayIntervalConstructor
 */

// Inspirace pro případné operace s rozsahy:
// - https://github.com/rotaready/moment-range#overlaps
// - https://github.com/rotaready/moment-range/blob/498b2c3ba1fc44f8867f7f881854d3e6e8af84ea/lib/moment-range.js
/**
 * Souvislý interval s granularitou dnů zadaný počátečním a koncovým dnem
 * (včetně) v lokální časové zóně. Koncový den musí být stejný nebo větší jako
 * počáteční, takže minimální délka intervalu je 1 den (a tedy interval o délce
 * 0 dní nelze reprezentovat).
 *
 * Pro stanovení, zda hledaný timestamp spadá do rozsahu intervalu, se počáteční
 * den sémanticky doplní o lokální čas 00:00:00 a ke koncovému dni se před
 * doplněním času přičte jeden den a tento koncový timestamp se bere vyjma.
 *
 * Konstruktor a statické konstrukční funkce vždy kontrolují validitu
 * start/end datumů a zda je end >= start, případně vyhodí TypeError.
 *
 * Tato třída ctí immutable přístup a jakákoliv modifikace vrací nový objekt.
 */
export default class DayInterval {
  /**
   * @protected
   * @type {typeof DayInterval['fromObject']}
   * @throws {TypeError}
   */
  constructor(o) {
    const { start, end } = o;
    // Aby isValid nevyhodilo nepředpokládanou chybu (TypeError z isLessOrEqual)
    // při nevalidních datumech a nedělali zbytečně 2x validaci, tak zaobalíme try/catch.
    let valid = false;
    try {
      valid = isValid(start, end);
    } catch {}
    if (!valid) {
      throw new TypeError(`new ${Stringify.call(this.constructor.name, o)}`);
    }

    /**
     * Počáteční den intervalu.
     * @type {DayDate}
     * @readonly
     */
    this.start = start;

    /**
     * Koncový den intervalu (včetně).
     * @type {DayDate}
     * @readonly
     */
    this.end = end;

    // Trik s definicí non-enumerable read-only property, aby byl TS spokojený.
    Object.defineProperty(this, 'days', { writable: true });
    /**
     * Délka intervalu v počtu dnů.
     * @readonly
     */
    this.days = diffDays(end, start) + 1;
    Object.defineProperty(this, 'days', {
      configurable: false,
      writable: false
    });

    // TODO: Zajištění immutability i pro dědičnost bez getterů, atd.
    // - https://stackoverflow.com/questions/37084169/object-freeze-in-constructor-with-subclasses/37085416#37085416
    if (new.target === DayInterval) {
      Object.freeze(this);
    }
  }

  /**
   * @param {DayDate} start
   * @param {DayDate} end
   * @returns {DayInterval}
   * @throws {TypeError}
   */
  static from(start, end) {
    return new DayInterval({ start, end });
  }

  /**
   * @param {Object} o
   * @param {DayDate} o.start Počáteční den intervalu.
   * @param {DayDate} o.end Koncový den intervalu (včetně).
   * @returns {DayInterval}
   * @throws {TypeError}
   */
  static fromObject(o) {
    return new DayInterval(o);
  }

  inspect() {
    return this.toString();
  }

  /**
   * @param {DayDate} end
   * @returns {this}
   * @throws {TypeError} Pokud dojde k vytvoření nevalidního intervalu.
   */
  setEnd(end) {
    return new /** @type {DayIntervalConstructor<this>} */ (this.constructor)({
      ...this,
      end
    });
  }

  /**
   * @param {DayDate} start
   * @returns {this}
   * @throws {TypeError} Pokud dojde k vytvoření nevalidního intervalu.
   */
  setStart(start) {
    return new /** @type {DayIntervalConstructor<this>} */ (this.constructor)({
      ...this,
      start
    });
  }

  toString() {
    return `DayInterval(${this.start}, ${this.end}) days=${this.days}`;
  }
}

/**
 * Zkontroluje, zda validní start a end tvoří validní interval.
 * @param {DayDate} start Validní DayDate pro začátek intervalu.
 * @param {DayDate} end Validní DayDate pro konec intervalu.
 * @returns {boolean}
 * @throws {TypeError} Pokud je start nebo end nevalidní.
 */
export function isValid(start, end) {
  return isLessOrEqual(start, end);
}
